// ─────────────────────────────────────────────
//  BarName — a person's name on a case bar, and (when the viewer may place
//  people) the control that opens the quick-actions menu for THAT person.
//
//  The owner's rule, chosen 2026-08-20: click the NAME → the quick-actions menu
//  (New Room · Takeover · Assign Supervising CRNA · Give Breaks · Home ·
//  Transfer). Click the rest of the bar → the case modal. The edges keep the
//  drag. One learnable rule, and in a room with two providers BOTH names are
//  triggers, each for its own person. Neither is decoration.
//
//  ⚠️ IT IS A `<button>`, A SIBLING OF THE BAR'S BUTTON, NEVER A CHILD OF IT.
//  See `CaseBarShell` for the structure. A button inside a button is invalid HTML
//  and in practice unreachable by both keyboard and screen reader.
//
//  ⚠️ AND IT IS ONLY A BUTTON WHEN THERE IS SOMETHING TO OPEN. A wall display
//  and an observer render the plain `aria-hidden` span this file has always
//  drawn. `menu == null` is byte-for-byte the old markup.
//
//  ⚠️ A NAME THE IMPORTER NEVER RESOLVED USED TO BE IN THAT LIST, AND IS NOT
//  ANY MORE. The owner reported those names as broken — from the operator's
//  side, a name that opens nothing is indistinguishable from one that is
//  faulty. `CaseBar` now offers the trigger regardless and `domain/assignChoices`
//  narrows the menu to the one option that needs no staff id.
//
//  ⚠️ A DRAG THAT ENDS ON THE NAME MUST NOT OPEN THE MENU. There is exactly one
//  suppression flag for that (`useBarDrag`'s `suppressClickRef`) and the caller
//  consumes it before calling `onOpen`. This file does not own a second one.
// ─────────────────────────────────────────────
import React from "react";
import S from "./caseBarStyles";

export default function BarName({
  // What the width actually allowed (`domain/caseBarLabel.fitBarName`).
  text,
  depart = "",
  // The line's own type rules — the seat line and the second line differ.
  style,
  // null → a plain span, exactly as before. Otherwise:
  //   { label, expanded, open, triggerRef }
  menu = null,
}) {
  // ⚠️ NOTHING BUT THIS PERSON GOES IN HERE, AND THAT IS A CORRECTION.
  // A `suffix` prop used to append the trainee's name to the seat holder's
  // label — inside this button. It read correctly and it was unusable: the
  // owner reported on 2026-09-09 that only the first of the two names could be
  // clicked, because there was only ever ONE control. A second person on the
  // bar is a second `BarName`, a sibling on `caseBarStyles.barNameLine`.
  const inner = (
    <>
      {text}
      {depart ? <span style={S.barDepart}> {depart}</span> : null}
    </>
  );

  if (!menu) {
    return (
      <span aria-hidden="true" style={style}>
        {inner}
      </span>
    );
  }

  return (
    <button
      type="button"
      ref={menu.triggerRef}
      data-tl-name=""
      // ⚠️ THE UNDEGRADED NAME. The visible text may have fallen all the way to
      // a bare surname on a 40px bar; the control still has to say who it acts
      // on. It is NOT `aria-hidden` — nothing focusable ever is.
      aria-label={menu.label}
      aria-haspopup="menu"
      aria-expanded={Boolean(menu.expanded)}
      // ⚠️ THE STACKING GOES LAST. `S.barLabel` (and the second line's rules)
      // carry `position: relative; zIndex: 1` — the same layer as the bar's
      // open-target — so spreading them after `S.nameTrigger` would silently
      // demote this control to the layer it has to sit ABOVE. It would still
      // work today, purely because it comes later in the DOM; it would stop
      // working the first time anything moved.
      style={{ ...S.nameTrigger, ...style, position: "relative", zIndex: 3 }}
      onClick={(e) => {
        e.stopPropagation();
        menu.open();
      }}
      onKeyDown={(e) => {
        // Enter / Space are a button's own activation; stop them reaching the
        // bar's open-target underneath, which would open the case modal too.
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
      // A pointerdown here is not the start of a bar drag.
      onPointerDown={(e) => e.stopPropagation()}
    >
      {inner}
    </button>
  );
}
